import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { GetUser } from "../../../Redux/Slices/User";
import { CreateSubscriptionOrder, GetOnePackage } from "../../../Redux/Slices/Package";
import toast from "react-hot-toast";
import Sidebar from "../../Common/Sidebar";
import EmployerLayout from "./Common/EmployerLayout";
import { FaArrowLeft, FaCheckCircle } from "react-icons/fa";
import "../../../asset/new/css/employer-modern.css";

const loadScript = (src) => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const Payment = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { userData } = useSelector((state) => state?.User);
  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(false);
  const userId = localStorage.getItem("id");

  useEffect(() => {
    dispatch(GetUser(userId));
    GetOnePackage(id).then(setPurchase);
    document.title = "Payment";
  }, [dispatch, id, userId]);

  // Final payable amount should be ₹100 (including GST).
  // Show base price as 100/1.18 and compute GST as the difference.
  const FINAL_PAYABLE = 100;
  const basePrice = purchase?.cost ? parseFloat(purchase.cost) : parseFloat((FINAL_PAYABLE / 1.18).toFixed(2));
  const gstAmount = parseFloat((FINAL_PAYABLE - basePrice).toFixed(2));
  const totalAmount = FINAL_PAYABLE;

  const displayRazorpay = async () => {
    setLoading(true);
    const isScriptLoaded = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
    if (!isScriptLoaded) {
      toast.error("Razorpay SDK failed to load. Are you online?");
      setLoading(false);
      return;
    }

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      currency: "INR",
      amount: totalAmount * 100,
      name: "CAJobPortal",
      description: "Thank you for Subscription.",
      handler: async (response) => {
        try {
          const data = await CreateSubscriptionOrder({
            paymentId: response.razorpay_payment_id,
            package: purchase._id,
            employer: userId,
            validity_day: purchase.validity_day,
            avail_jobposting: purchase.no_job_posting,
            avail_cvsearch: purchase.no_cv_search,
          });
          if (data?.success) navigate("/employer/dashboard");
          else toast.error("Subscription creation failed. Please contact support.");
        } catch (error) {
          console.error("Error creating subscription:", error);
          toast.error("An error occurred while processing your subscription. Please try again.");
        } finally {
          setLoading(false);
        }
      },
      prefill: { contact: userData?.contact || "", email: userData?.email },
      modal: {
        ondismiss: () => {
          setLoading(false);
          toast.error("Payment cancelled by user.");
        },
      },
    };

    new window.Razorpay(options).open();
  };

  return (
    <div className="employer-layout">
      <Sidebar />
      <EmployerLayout breadcrumbs={['Employer', 'Packages', 'Payment']}>
        {loading && (
          <div className="employer-loading-overlay">
            <div className="employer-spinner"></div>
          </div>
        )}

        <div className="employer-page-header">
          <h1 className="employer-page-title">Complete Payment</h1>
        </div>

        <div className="employer-form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <div className="employer-card">
            <div className="employer-card-header">
              <h3 className="employer-card-title">Package Details</h3>
            </div>
            <div className="employer-card-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div className="employer-flex employer-justify-between">
                  <span style={{ color: 'var(--employer-text-muted)' }}>Package Name</span>
                  <span style={{ fontWeight: 600 }}>{purchase?.name}</span>
                </div>
                <div className="employer-flex employer-justify-between">
                  <span style={{ color: 'var(--employer-text-muted)' }}>Package Details</span>
                  <span style={{ fontWeight: 600 }}>{purchase?.offer}</span>
                </div>
                <div className="employer-flex employer-justify-between">
                  <span style={{ color: 'var(--employer-text-muted)' }}>Validity</span>
                  <span style={{ fontWeight: 600 }}>{purchase?.validity_day} Days</span>
                </div>
                <div className="employer-flex employer-justify-between">
                  <span style={{ color: 'var(--employer-text-muted)' }}>Price (ex. GST)</span>
                  <span style={{ fontWeight: 600 }}>₹{basePrice}</span>
                </div>
                <div className="employer-flex employer-justify-between">
                  <span style={{ color: 'var(--employer-text-muted)' }}>GST (18%)</span>
                  <span style={{ fontWeight: 600 }}>₹{gstAmount}</span>
                </div>
                <div className="employer-flex employer-justify-between">
                  <span style={{ color: 'var(--employer-text-muted)' }}>Discount (%)</span>
                  <span style={{ fontWeight: 600 }}>{purchase?.discount || "0"}%</span>
                </div>
                <div className="employer-flex employer-justify-between">
                  <span style={{ color: 'var(--employer-text-muted)' }}>Total Cost</span>
                  <span style={{ fontWeight: 600 }}>₹{totalAmount}</span>
                </div>
                <hr style={{ margin: '0.5rem 0' }} />
                <div className="employer-flex employer-justify-between">
                  <span style={{ fontWeight: 600, fontSize: '1.125rem' }}>Total Amount</span>
                  <span style={{ fontWeight: 700, fontSize: '1.125rem', color: 'var(--employer-primary)' }}>₹{totalAmount}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="employer-card">
            <div className="employer-card-header">
              <h3 className="employer-card-title">Customer Details</h3>
            </div>
            <div className="employer-card-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div className="employer-flex employer-justify-between">
                  <span style={{ color: 'var(--employer-text-muted)' }}>Name</span>
                  <span style={{ fontWeight: 600 }}>{userData?.name}</span>
                </div>
                <div className="employer-flex employer-justify-between">
                  <span style={{ color: 'var(--employer-text-muted)' }}>Email</span>
                  <span style={{ fontWeight: 600 }}>{userData?.email}</span>
                </div>
                <div className="employer-flex employer-justify-between">
                  <span style={{ color: 'var(--employer-text-muted)' }}>Phone Number</span>
                  <span style={{ fontWeight: 600 }}>{userData?.mobile}</span>
                </div>
                <div className="employer-flex employer-justify-between">
                  <span style={{ color: 'var(--employer-text-muted)' }}>Designation</span>
                  <span style={{ fontWeight: 600 }}>{userData?.experience[0]?.designation}</span>
                </div>
                <div className="employer-flex employer-justify-between">
                  <span style={{ color: 'var(--employer-text-muted)' }}>Company Name</span>
                  <span style={{ fontWeight: 600 }}>{userData?.experience[0]?.company_name}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="employer-flex employer-gap-2" style={{ justifyContent: 'center', marginTop: '1.5rem' }}>
          <button 
            onClick={() => navigate(-1)} 
            className="employer-btn employer-btn-secondary"
            disabled={loading}
          >
            <FaArrowLeft /> Back
          </button>
          <button 
            onClick={displayRazorpay} 
            className="employer-btn employer-btn-primary employer-btn-lg"
            disabled={loading}
          >
            <FaCheckCircle /> Proceed to Pay ₹{totalAmount}
          </button>
        </div>
      </EmployerLayout>
    </div>
  );
};

export default Payment;